import type {
  Problem,
  ProblemWithAngles,
  AISuggestion,
  CreateProblemRequest,
  CreateAngleRequest,
  UpdateProgressRequest,
  ChatHistory,
  SendMessageRequest,
  AngleTodo,
  AngleResource,
  CreateTodoRequest,
  UpdateTodoRequest,
  CreateResourceRequest,
  ProblemContext,
  SaveContextRequest
} from '@attack100/shared';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

export const api = {
  // Problems
  getProblems: () =>
    fetchJSON<{ problems: Problem[] }>('/problems'),

  getProblem: (id: number) =>
    fetchJSON<{ problem: ProblemWithAngles }>(`/problems/${id}`),

  createProblem: (data: CreateProblemRequest) =>
    fetchJSON<{ problem: Problem }>('/problems', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProblem: (id: number, data: CreateProblemRequest) =>
    fetchJSON<{ problem: Problem }>(`/problems/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteProblem: (id: number) =>
    fetchJSON<{ success: boolean }>(`/problems/${id}`, {
      method: 'DELETE',
    }),

  generateAngles: (problemId: number) =>
    fetchJSON<{ problem_id: number; angles: any[]; count: number }>(`/problems/${problemId}/generate-angles`, {
      method: 'POST',
    }),

  generateAngle: (problemId: number, personaId: string, variation: number) =>
    fetchJSON<{ angle: any }>(`/problems/${problemId}/generate-angle`, {
      method: 'POST',
      body: JSON.stringify({ personaId, variation }),
    }),

  // Angles
  createAngle: (problemId: number, data: CreateAngleRequest) =>
    fetchJSON<{ angle: any }>(`/problems/${problemId}/angles`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAngleProgress: (angleId: number, data: UpdateProgressRequest) =>
    fetchJSON<{ progress: any }>(`/angles/${angleId}/progress`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  activateAngle: (angleId: number) =>
    fetchJSON<{ angle: any }>(`/angles/${angleId}/activate`, {
      method: 'POST',
    }),

  deleteAngle: (angleId: number) =>
    fetchJSON<{ success: boolean }>(`/angles/${angleId}`, {
      method: 'DELETE',
    }),

  // Suggestions
  getSuggestion: (problemId: number) =>
    fetchJSON<{ suggestion: AISuggestion | null; message?: string }>(`/suggestions/${problemId}`),

  // Chat
  getChatHistory: (angleId: number) =>
    fetchJSON<ChatHistory>(`/angles/${angleId}/chat`),

  sendMessage: (angleId: number, data: SendMessageRequest) =>
    fetchJSON<{ user_message: any; assistant_message: any }>(`/angles/${angleId}/chat`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Todos
  getTodos: (angleId: number) =>
    fetchJSON<{ angle_id: number; todos: AngleTodo[]; total: number }>(`/angles/${angleId}/todos`),

  createTodo: (angleId: number, data: CreateTodoRequest) =>
    fetchJSON<AngleTodo>(`/angles/${angleId}/todos`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTodo: (todoId: number, data: UpdateTodoRequest) =>
    fetchJSON<AngleTodo>(`/todos/${todoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteTodo: (todoId: number) =>
    fetchJSON<{ success: boolean }>(`/todos/${todoId}`, {
      method: 'DELETE',
    }),

  // Resources
  getResources: (angleId: number) =>
    fetchJSON<{ angle_id: number; resources: AngleResource[]; total: number }>(`/angles/${angleId}/resources`),

  createResource: (angleId: number, data: CreateResourceRequest) =>
    fetchJSON<AngleResource>(`/angles/${angleId}/resources`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteResource: (resourceId: number) =>
    fetchJSON<{ success: boolean }>(`/resources/${resourceId}`, {
      method: 'DELETE',
    }),

  // Problem Context
  getContext: (problemId: number) =>
    fetchJSON<{ context: ProblemContext | null }>(`/problems/${problemId}/context`),

  saveContext: (problemId: number, data: SaveContextRequest) =>
    fetchJSON<{ context: ProblemContext }>(`/problems/${problemId}/context`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateContext: (problemId: number, data: SaveContextRequest) =>
    fetchJSON<{ context: ProblemContext }>(`/problems/${problemId}/context`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};
